import Vue from 'vue';
import Router from 'vue-router';
import InstantSearch from 'vue-instantsearch';
import appStore from '@/store';
import UserIndexSearchService from '@/views/repository/user/search/UserIndexSearchService';

Vue.use(InstantSearch);
Vue.use(Router);

function load(component) {
  // '@' is aliased to src/components
  return () => import(`@/${component}.vue`);
}

let previousRoute = '';
const routes = [
  {
    path: '/',
    component: load('views/parts/Layout'),
    children: [
      {
        path: '/',
        name: 'home',
        beforeEnter: async (to, _, next) => {
          next({ name: 'admin_recruitments_list' });
        },
        meta: {public: true},
      },
      {
        path: '/admin/indexing',
        name: 'indexing_dashboard',
        component: load('views/Admin/IndexingDashboard'),
      },

      //  Organization
      {
        path: '/setup/organizations',
        name: 'organizations_repository',
        component: load('views/setup/organization/ListPage'),
        meta: {visibleWhenOrganizationSelected: false},
      },

      {
        path: '/setup/organization/create',
        name: 'organizations_create',
        component: load('views/setup/organization/Edit'),
        meta: {visibleWhenOrganizationSelected: false},
      },
      {
        path: '/setup/organization/edit/:id',
        name: 'organizations_edit',
        component: load('views/setup/organization/Edit'),
        meta: {visibleWhenOrganizationSelected: false},
      },
      {
        path: '/setup/:organization_code/organization/create/',
        name: 'employer_create',
        component: load('views/setup/organization/Edit'),
        meta: {visibleWhenOrganizationSelected: true},
        props: route => ({
          organizationCode: route.params.organization_code,
        }),
      },
      {
        path: '/setup/:organization_code/organization/edit/:id',
        name: 'employer_edit',
        component: load('views/setup/organization/Edit'),
        meta: {visibleWhenOrganizationSelected: true},
        props: route => ({
          organizationCode: route.params.organization_code,
        }),
      },

      // Administrator
      {
        path: '/repository/administrator',
        name: 'administrators_repository',
        component: load('views/repository/administrator/List'),
        meta: {visibleWhenOrganizationSelected: false},
      },
      {
        path: '/repository/administrator/create',
        name: 'administrators_create',
        component: load('components/common/crud/CreateUser'),
        meta: {visibleWhenOrganizationSelected: false},
        props: {createAdmin: true},
      },

      // Handicap Users
      {
        path: '/repository/handicap-users',
        name: 'handicap_users_repository',
        component: load('views/repository/user/handicap/UsersHandicapInfoTable'),
        meta: {visibleWhenOrganizationSelected: false},
      },

      // Front-office users
      {
        path: '/setup/organization/:organization_code/front-users/:userId/detail',
        name: 'front_user_detail_for_organization',
        component: load('views/repository/user/detail/Detail'),
        meta: {visibleWhenOrganizationSelected: true},
        props: route => ({
          organizationCode: route.params.organization_code,
          ...route.params,
        }),
      },
      {
        path: '/repository/front-users',
        name: 'front_user_list',
        component: load('views/repository/user/List'),
        meta: {visibleWhenOrganizationSelected: false},
      },
      {
        path: '/repository/front-users/:userId/detail',
        name: 'front_user_detail',
        component: load('views/repository/user/detail/Detail'),
        meta: {visibleWhenOrganizationSelected: false},
        props: route => ({
          ...route.params,
        }),
      },
      // Recruitment
      {
        path: '/setup/organization/:organization_code/recruitment/list',
        name: 'recruitments_list',
        component: load('views/setup/recruitment/list/RecruitmentList'),
        props: route => ({
          organizationCode: route.params.organization_code,
        }),
        meta: {visibleWhenOrganizationSelected: true},
      },
      {
        path: '/recruitment/list',
        name: 'admin_recruitments_list',
        component: load('views/setup/recruitment/list/RecruitmentList'),
        meta: {visibleWhenOrganizationSelected: false},
      },
      {
        path: '/setup/organization/:organization_code/recruitment/:recruitment_id/result',
        name: 'recruitment_result',
        component: load('views/setup/recruitment/detail/result/List'),
        meta: {visibleWhenOrganizationSelected: true},
        props: route => ({
          ...route.params,
          recruitment_id: Number(route.params.recruitment_id),
        }),
      },
      {
        path: '/setup/organization/:organization_code/recruitment/:recruitmentCode/close',
        name: 'close_recruitment',
        component: load('views/setup/recruitment/StateChange'),
        meta: {visibleWhenOrganizationSelected: true},
        props: route => ({
          ...route.params,
          targetState: 'SELECTION',
        }),
      },
      {
        path: '/setup/organization/:organization_code/recruitment/:recruitmentCode/terminate',
        name: 'terminate_recruitment',
        component: load('views/setup/recruitment/StateChange'),
        meta: {visibleWhenOrganizationSelected: true},
        props: route => ({
          ...route.params,
          targetState: 'CLOSED',
        }),
      },

      // Candidature
      {
        path: '/setup/organization/:organization_code/candidature/:candidature_id',
        name: 'candidature_details',
        component: load('views/setup/candidature/detail/CandidatureDetail'),
        props: route => ({
          ...route.params,
          candidature_id: Number(route.params.candidature_id),
        }),
        meta: {visibleWhenOrganizationSelected: true},
      },
      {
        path: '/setup/organization/:organization_code/user-index',
        name: 'organization_user_index',
        component: load('views/repository/user/search/List'),
        props: route => ({initialService: new UserIndexSearchService(route.params.organizationCode)}),
        meta: {visibleWhenOrganizationSelected: true},
      },

      {
        path: '/repository/user-index',
        name: 'user-index',
        component: load('views/repository/user/search/AllUserPage'),
        meta: {visibleWhenOrganizationSelected: false},
      },
      {
        path: '/repository/user/bulk-cv-processing',
        name: 'bulk_cv_processing',
        component: load('views/repository/user/bulk-cv/BulkCVProcessingPage'),
        meta: {visibleWhenOrganizationSelected: false},
      },
      {
        path: '/repository/user-edit/:userId',
        name: 'user-edit',
        component: load('views/repository/user/edit/EditUserPage'),
        meta: {visibleWhenOrganizationSelected: false},
        props: true,
      },
      // Mass Scrape
      {
        path: '/admin/mass-scrape',
        name: 'mass_scrape',
        component: load('views/setup/mass-scrape/MassScrapePage'),
        meta: {visibleWhenOrganizationSelected: false},
      },
      // Gestion ROME
      {
        path: '/admin/rome-management',
        name: 'rome_management',
        component: load('views/Admin/RomeManagementPage'),
        meta: {visibleWhenOrganizationSelected: false},
      },
      {
        path: '/admin/occupation-diagnostic',
        name: 'occupation_diagnostic',
        component: load('views/Admin/OccupationDiagnosticPage'),
        meta: {visibleWhenOrganizationSelected: false},
      },
    ],
  },

  // Not found
  {
    path: '*',
    component: load('views/errors/404'),
    props: () => ({
      previousRoute,
    }),
    meta: {public: true},
  },

  // Unauthorized
  {
    path: '*',
    name: 'unauthorized',
    component: load('views/errors/401'),
    meta: {public: true},
  },
];

const router = new Router({
  mode: 'history',
  scrollBehavior: () => ({y: 0}),
  routes,
});

router.afterEach((to, from) => {
  previousRoute = from?.fullPath;
});

router.beforeEach((to, from, next) => {
  document.title = '#jenesuisPASunCV';
  next();
});

router.beforeEach(async (to, from, next) => {
  next();
});

router.beforeEach(async (to, from, next) => {
  if (!to.meta.visibleWhenOrganizationSelected) {
    next();
    return;
  }

  const loadedOrganization = appStore.getters.organizationLoaded;
  if (!loadedOrganization
    || (to.params.organization_code !== loadedOrganization.code
      // Next line: ensure this is NOT an employer
      && !!loadedOrganization.organizationType)
  ) {
    try {
      const selectedOrga = (await Vue.$api.getOrganizationByCode(to.params.organization_code)).data;
      appStore.commit('loadOrganization', selectedOrga);
    } catch (e) {
      // Failure => go to home
      next('/');
      return;
    }
  }
  next();
});

Vue.$router = router;

export default router;
